import { Component, ElementRef, Input, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { buildFormatTreeFromString, FormatTreeNode } from '../../../shared/helpers/markdown';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faBold } from '@fortawesome/pro-solid-svg-icons/faBold';
import { faItalic } from '@fortawesome/pro-solid-svg-icons/faItalic';
import { faStrikethrough } from '@fortawesome/pro-solid-svg-icons/faStrikethrough';
import { faCode } from '@fortawesome/pro-solid-svg-icons/faCode';
import { faEyeSlash } from '@fortawesome/pro-solid-svg-icons/faEyeSlash';
import { BehaviorSubject } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'andor-rte',
  standalone: true,
  imports: [CommonModule, TranslatePipe, FontAwesomeModule],
  templateUrl: './rte.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './rte.scss',
})
export class RTE {
  protected readonly faBold = faBold;
  protected readonly faItalic = faItalic;
  protected readonly faStrikethrough = faStrikethrough;
  protected readonly faCode = faCode;
  protected readonly faEyeSlash = faEyeSlash;
  @ViewChild('editable') _editable!: ElementRef;
  @ViewChild('selectionEdit') _selectionEdit!: ElementRef;
  constructor() {}
  @Input() paste!: (event: ClipboardEvent) => void;
  private selectedRange = new BehaviorSubject<Range[]>([]);

  private prevFormatTree = new BehaviorSubject<FormatTreeNode>(new FormatTreeNode(''));
  private disabled = false;

  private dedent(segments: string) {
    const lines = segments.trimEnd().split('\n');

    const minLeadingChars =
      lines
        .filter((l) => l !== '')
        .reduce((acc: string | undefined, line) => {
          const leader = line.match(/^\s*/)?.[0] || '';
          console.log(`'${leader}'`);
          if (acc === undefined) {
            return leader;
          }

          let common = '';
          for (let i = 0; i < Math.min(acc.length, leader.length); i++) {
            if (acc[i] !== leader[i]) {
              break;
            }
            common += acc[i];
          }

          return common;
        }, undefined)?.length || 0;

    return lines.map((line) => line.substring(minLeadingChars)).join('\n');
  }

  private hasMonospaceFont(node: HTMLElement) {
    const fontFamily = node.style.fontFamily;
    return fontFamily && fontFamily.toLowerCase().includes('monospace');
  }

  // E.g. Firefox devtools don't set inline styles but use classes.
  // Fortunately the class name includes 'monospace'.
  private hasMonospaceClass(node: HTMLElement) {
    let hasClass = false;
    node.classList.forEach((clas) => {
      hasClass ||= clas.toLowerCase().includes('monospace');
    });
    return hasClass;
  }

  // Recursively check all elements
  private checkMonospaceInDOM(node: Element) {
    if (this.hasMonospaceFont(node as HTMLElement) || this.hasMonospaceClass(node as HTMLElement)) {
      return true;
    }
    for (const child of node.children) {
      if (this.checkMonospaceInDOM(child)) {
        return true;
      }
    }
    return false;
  }

  protected _paste(event: ClipboardEvent) {
    this.paste(event);
    if (event.defaultPrevented) return;

    if (!event.clipboardData) return;
    const types = event.clipboardData.types;
    if (!types.includes('text/plain')) return;

    let text = event.clipboardData.getData('text/plain');
    let isCode = false;
    if (types.includes('text/html')) {
      // Parse into an inert document: markup assigned to an element of the
      // live document starts loading its images, and clipboard HTML from
      // Office apps references `file:///…/clip_image001.png`, which Firefox
      // reports as a security error.
      const html = event.clipboardData.getData('text/html');
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      isCode = this.checkMonospaceInDOM(parsed.documentElement);
    }

    if (isCode) {
      if (text.includes('\n')) {
        text = '```\n' + this.dedent(text) + '\n```';
      } else {
        text = '`' + text.trim() + '`';
      }
    }
    const selection = document.getSelection()!;
    let temp = document.createTextNode(text);

    for (let i = 0; i < selection.rangeCount; i++) {
      const range = selection.getRangeAt(i);
      temp = document.createTextNode(text);
      if (!range.collapsed) range.deleteContents();
      range.insertNode(temp);
    }

    const range = document.createRange();
    const sel = window.getSelection()!;
    range.setStart(temp, text.length);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);

    event.preventDefault();

    const trigger = new InputEvent('input');
    this._editable.nativeElement.dispatchEvent(trigger);
  }

  private buildNodePath(src: Node, target: Node) {
    const path = [];
    let curr = src;
    while (curr !== target) {
      let i = 0;
      for (const c of curr.parentNode!.childNodes!) {
        if (c === curr) break;
        i++;
      }
      curr = curr.parentNode!;
      path.push(i);
    }
    return path;
  }

  handleBeforeInput(event: Event) {
    const input = event as InputEvent;
    if (input.inputType !== 'insertLineBreak' || input.isComposing || !input.cancelable) return;

    const target = this._editable.nativeElement as HTMLElement;
    const selection = document.getSelection();
    if (!selection?.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (!target.contains(range.commonAncestorContainer)) return;

    // Browsers insert <br> inside inline-code previews. Our source is
    // textContent, so insert the newline as text before that can happen.
    input.preventDefault();
    range.deleteContents();
    const newline = document.createTextNode('\n');
    range.insertNode(newline);
    range.setStartAfter(newline);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    target.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertLineBreak' }));
  }

  handleTextInput(event: Event) {
    if ((event as InputEvent).isComposing) return;

    if (!event.target) return;
    const target = event.target as HTMLDivElement;

    if (target.textContent === '') {
      target.innerHTML = '';
      return;
    }

    let range = document.getSelection()!.getRangeAt(0);
    range = range.cloneRange();
    range.collapse();
    const path = this.buildNodePath(range.startContainer, target);
    const clone = target.cloneNode(true);
    let curr = clone;
    path.reverse().forEach((n) => {
      curr = curr.childNodes![n];
    });
    range.setStart(curr, range.startOffset);
    range.setEnd(curr, range.endOffset);

    const temp = document.createTextNode('\0');
    range.insertNode(temp);

    let result = clone.textContent || '';
    result = result.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');

    const formatTree = buildFormatTreeFromString(result);
    // This might lead to unexpected problems on newlines...
    // if (
    //   this.prevFormatTree.value &&
    //   formatTree.equalsInStructure(this.prevFormatTree.value)
    // ) {
    //   return;
    // }

    // we only need to set the tree if the shape changed, not if only the content changed.
    result = formatTree.assembleString({
      enableSyntaxHighlighting: false,
      setInteractive: false,
      codeblockCopy: false,
    });
    this.prevFormatTree.next(formatTree);

    result = result.replace('\0', "<span id='caret'></span>");
    console.log(result);
    target.innerHTML = result;

    target.appendChild(document.createTextNode(''));
    // A trailing newline needs a final empty line for the caret. A <br>
    // supplies that line without adding another character to the source.
    if (target.textContent?.endsWith('\n')) target.appendChild(document.createElement('br'));

    range = document.createRange();
    const sel = window.getSelection()!;
    const caret = document.getElementById('caret');
    if (!caret) return;
    range.setStart(caret, 0);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    caret.parentNode!.removeChild(caret);
  }

  ngAfterViewInit() {
    document.onselectionchange = () => this.handleSelection();
  }

  handleSelection() {
    const rangeSelected = () => {
      const selection = document.getSelection();

      if (!selection || !this._editable.nativeElement.contains(selection.anchorNode)) {
        return false;
      }
      if (selection.isCollapsed) {
        return false;
      }

      return selection;
    };

    const selection = rangeSelected();
    if (selection) {
      this._selectionEdit.nativeElement.classList.remove('hidden');
      const ranges = [];
      for (let i = 0; i < selection.rangeCount; i++) {
        ranges.push(selection.getRangeAt(i));
      }
      this.selectedRange.next(ranges);
    } else {
      setTimeout(() => {
        if (!rangeSelected()) {
          this._selectionEdit.nativeElement.classList.add('hidden');
          this.selectedRange.next([]);
        }
      }, 1000);
    }
  }

  protected shortcutChangeSelection(
    event: Event,
    type: 'bold' | 'italic' | 'strike' | 'code' | 'hidden'
  ) {
    event.preventDefault();
    this.changeSelection(type);
  }

  protected changeSelection(
    type: 'bold' | 'italic' | 'strike' | 'code' | 'hidden',
    useStoredRanges: boolean = false
  ) {
    let ranges;
    if (!useStoredRanges) {
      const selection = document.getSelection();

      if (!selection || !this._editable.nativeElement.contains(selection?.anchorNode)) {
        return;
      }

      if (selection.isCollapsed) return;

      ranges = [];
      for (let i = 0; i < selection.rangeCount; i++) {
        ranges.push(selection.getRangeAt(i));
      }
    } else {
      ranges = this.selectedRange.value;
      if (ranges.length === 0) return;
    }

    const modifier = {
      bold: '**',
      italic: '*',
      strike: '~~',
      code: '`',
      hidden: '||',
    };

    for (const range of ranges) {
      let editMe = range.cloneContents().textContent;
      if (!editMe) continue;

      let handled = false;

      if (type === 'code') {
        const target = this._editable.nativeElement;
        const clone = target.cloneNode(true);
        const start = range.cloneRange();

        let path = this.buildNodePath(range.startContainer, target);
        let curr = clone;
        path.reverse().forEach((n) => {
          curr = curr.childNodes![n];
        });
        start.setStart(curr, range.startOffset);
        path = this.buildNodePath(range.endContainer, target);
        curr = clone;
        path.reverse().forEach((n) => {
          curr = curr.childNodes![n];
        });
        start.setEnd(curr, range.endOffset);
        const end = start.cloneRange();

        start.collapse(true);
        end.collapse(false);

        const sMarker = document.createTextNode('\0');
        start.insertNode(sMarker);
        const eMarker = document.createTextNode('\0');
        end.insertNode(eMarker);

        if (clone.textContent.match(/(^\0)|(\0\n)(.|\n)*\0$/gm)) {
          handled = true;
          let beg = '```\n';
          if (editMe.startsWith('\n')) {
            beg = '\n```';
          }
          editMe = beg + editMe + '\n```';
        }
      }

      if (!handled) {
        const m = modifier[type];
        editMe = editMe.replaceAll('\n', m + '\n' + m);
        editMe = modifier[type] + editMe + modifier[type];
      }

      const replacement = document.createTextNode(editMe);
      range.extractContents();
      range.insertNode(replacement);
    }

    const trigger = new InputEvent('input');
    this._selectionEdit.nativeElement.classList.add('hidden');
    this.selectedRange.next([]);
    this._editable.nativeElement.dispatchEvent(trigger);
  }

  public clear() {
    this._editable.nativeElement.innerHTML = '';
  }

  public getContent() {
    return this._editable.nativeElement.textContent;
  }

  public setDisabled() {
    this.disabled = true;
    this._editable.nativeElement.contenteditable = false;
    this._editable.nativeElement.style.cursor = 'progress';
  }

  public setEnabled() {
    this._editable.nativeElement.contenteditable = true;
    this._editable.nativeElement.style.cursor = 'initial';
    this.disabled = false;
  }

  public isDisabled() {
    return this.disabled;
  }
}
